import React, { useState } from 'react';
import { ChevronDown, Building2, Shield, Zap, Wifi, Truck, Package, Car, AlertTriangle, Leaf, Settings } from 'lucide-react';

const Amenities = ({ t }) => {
  const [activeCategory, setActiveCategory] = useState('businessCenter');

  const categories = [
    { key: 'businessCenter', icon: Building2 },
    { key: 'security', icon: Shield },
    { key: 'infrastructure', icon: Settings },
    { key: 'utilities', icon: Zap },
    { key: 'connectivity', icon: Wifi },
    { key: 'truckAccess', icon: Truck },
    { key: 'loading', icon: Package },
    { key: 'parking', icon: Car },
    { key: 'emergency', icon: AlertTriangle },
    { key: 'sustainability', icon: Leaf }
  ].map(cat => ({
    ...cat,
    data: t.amenities.categories[cat.key]
  }));

  const toggleCategory = (key) => {
    setActiveCategory(activeCategory === key ? null : key);
  };

  const activeItem = categories.find(c => c.key === activeCategory);

  return (
    <section id="amenities" data-testid="amenities-section" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
      <div className="container mx-auto px-5 sm:px-6 lg:px-8 max-w-6xl">
        {/* Hero Image - Park Amenities Pavilion */}
        <div className="mb-8 lg:mb-12 -mx-5 sm:-mx-6 lg:mx-0 overflow-hidden lg:rounded-lg">
          <div className="relative">
            <img
              src="/images/renders/HA03.jpeg"
              alt="Hexaparks outdoor pavilion and common areas"
              className="w-full h-[240px] sm:h-[320px] lg:h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-50 via-transparent to-transparent" />
          </div>
        </div>

        {/* Header */}
        <div className="mb-8 lg:mb-12">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-900 mb-3 leading-snug">
            {t.amenities.title}
          </h2>
          <p className="text-sm text-gray-500 max-w-xl">
            {t.amenities.subtitle}
          </p>
        </div>
        
        {/* Desktop: Two-column layout */}
        <div className="hidden lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Left: Category List */}
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="space-y-px bg-gray-200">
              {categories.map((category) => {
                const Icon = category.icon;
                const isActive = activeCategory === category.key;
                return (
                  <button
                    key={category.key}
                    data-testid={`amenity-desktop-${category.key}`}
                    onClick={() => setActiveCategory(category.key)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
                      isActive ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-600 hover:bg-white'
                    }`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-gray-700' : 'text-gray-400'}`} />
                    <span className="text-sm">{category.data.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Right: Detail Panel */}
          <div className="lg:col-span-7 xl:col-span-8">
            {activeItem && (
              <div className="bg-white border border-gray-200 p-6 xl:p-8">
                <div className="flex items-start gap-3 mb-4">
                  {React.createElement(activeItem.icon, { className: 'w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5' })}
                  <h3 className="text-base font-medium text-gray-900">{activeItem.data.title}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed pl-8">
                  {activeItem.data.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile/Tablet: Accordion layout */}
        <div className="lg:hidden space-y-px bg-gray-200">
          {categories.map((category) => {
            const Icon = category.icon;
            const isExpanded = activeCategory === category.key;
            return (
              <div key={category.key} className="bg-white">
                <button
                  data-testid={`amenity-mobile-${category.key}`}
                  onClick={() => toggleCategory(category.key)}
                  className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isExpanded ? 'text-gray-700' : 'text-gray-400'}`} />
                    <span className={`text-sm ${isExpanded ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                      {category.data.title}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
                
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1">
                    <p className="text-sm text-gray-600 leading-relaxed pl-7">
                      {category.data.description}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Amenities;
